import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";

const TimerContext = createContext();

const TWENTY_MIN = 1800;

export const TimerProvider = ({ children }) => {
  const timeLeftRef = useRef(TWENTY_MIN); // Stores timeLeft without causing re-renders
  const runningRef = useRef(false); // Stores running state without triggering re-renders
  const intervalRef = useRef(null); // Stores interval ID

  // Tick function to update time without causing a re-render
  const tick = () => {
    if (runningRef.current && timeLeftRef.current > 0) {
      timeLeftRef.current -= 1;
    } else {
      stopTimer(); // Stop when time reaches 0
    }
  };

  // Start timer if not already running
  const startTimer = useCallback(() => {
    if (!runningRef.current) {
      runningRef.current = true;
      intervalRef.current = setInterval(tick, 1000);
    }
  }, []);

  // Stop timer if running
  const stopTimer = useCallback(() => {
    runningRef.current = false;
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  // Reset timer without causing re-renders
  const resetTimer = useCallback(() => {
    timeLeftRef.current = TWENTY_MIN;
    runningRef.current = false;
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  // Set a specific time and start timer if needed
  const setTimeLeft = useCallback(
    (time) => {
      timeLeftRef.current = time;
      if (!runningRef.current) {
        startTimer();
      }
    },
    [startTimer]
  );

  // Cleanup effect to clear the interval on unmount
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <TimerContext.Provider
      value={{
        getTimeLeft: () => timeLeftRef.current, // Returns current time without causing re-renders
        startTimer,
        stopTimer,
        resetTimer,
        setTimeLeft,
        isRunning: () => runningRef.current, // Returns running state without causing re-renders
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
};

export default useTimer;
